import './index.css'
import React, {useContext, useEffect, useRef, useState} from "react";
import CountDownBar from "./component/CountDownBar/CountDownBar.tsx";
import GridLetterCard from "./component/GridLetterCard/GridLetterCard.tsx";
import Keyboard from "./component/Keyboard/Keyboard.tsx";
import VsPlayGameEndDialog from "./component/VsPlayGameEndDialog.tsx";
import {PageEnum, PagePropsWithVs} from "../../../../types/IPage.ts";
import {ThemeContext} from "../../../../providers/themeContext/theme-context.tsx";
import useLoading from "../../../../providers/loadingContext/useLoading.ts";
import {useMusicContext} from "../../../../providers/musicProvider/music-player-context.tsx";
import SettingModal from "../../../home/component/SettingModal.tsx";
import PlayTipModal from "../../../home/component/PlayTipsModal.tsx";
import {apiService} from "../../../../common/utils/http/api-service.ts";
import {IVSPlayerGameResult, IVSPlayerRoomInfo} from "../../../../common/utils/http/IResponse.ts";
import debounce from "lodash/debounce";
import {GAEvents} from "../../../../common/utils/GA4/GAEvents.ts";

// vs-player游戏
const VsPlayerGame: React.FC<PagePropsWithVs> = ({switchPage, vsPlayerInfo}) => {
    // 主题全局管理器
    const {imgUrls} = useContext(ThemeContext);
    // 加载中弹窗
    const {showLoading, hideLoading} = useLoading();
    // 全局音乐provider
    const musicManager = useMusicContext();
    const targetRef = useRef<HTMLDivElement>(null)
    // 提示的字母和位置
    const [hintLetter, setHintLetter] = useState<{ letter: string, state: number }>({letter: '', state: 0})
    // 是否已经提示过了
    const [hasHint, setHasHint] = useState<boolean>(false)
    // 是否显示游戏失败的弹窗提示
    const [showGameEndTip, setShowGameEndTip] = useState<boolean>(false)
    // 是否显示游戏提示
    const [showPlayTip, setShowPlayTip] = useState<boolean>(false)
    // 是否弹出设置弹窗
    const [showSetting, setShowSetting] = useState<boolean>(false)
    // 已经输入的单词列表
    const [inputWords, setInputWords] = useState<string[]>([])
    // 正确的单词
    const [correctWord, setCorrectWord] = useState<string | null>(null)
    // 已经输入的单词列表里所有错误的字母，不可重复
    const [wrongLetters, setWrongLetters] = useState<string[]>([])
    // 倒计时的秒数时间
    const [countDownSeconds, setCountDownSeconds] = useState<number>(1800)
    // 游戏状态，0未开始，1开始，2结束【输了】，3结束【赢了】
    const [gameState, setGameState] = useState<number>(0)
    // 游戏弹窗结果
    const [gameOverResult, setGameOverResult] = useState<IVSPlayerRoomInfo | null>(null)

    // 进入页面就开始加载正确单词
    useEffect(() => {
        setCorrectWord(vsPlayerInfo?.correctWord ?? '')
        // GA 埋点
        GAEvents.createMatchPlay()
    }, []);

    // 上传游戏数据
    const sendVsPlayerGameStatus = debounce(async (status, words) => {
        try {

            showLoading()
            const params: IVSPlayerGameResult = {
                roomId: vsPlayerInfo?.id ?? '',
                guessTimes: words.length,
                playTime: status === 3 ? 1800 - countDownSeconds : 1800,
                totalGuessWord: JSON.stringify(words),
                win: status === 3
            }
            const response = await apiService.submitVsPlayerResult(params)
            if (response.code === 200) {
                setGameOverResult(response.data);
            }
        } finally {
            hideLoading()
        }
    }, 100)


    useEffect(() => {
        // 游戏失败
        if (gameState === 2) {
            sendVsPlayerGameStatus(2, inputWords);
            // 延迟1秒显示
            setTimeout(() => {
                musicManager.playFailSound()
                setShowGameEndTip(true)
            }, 3000);
        }

        // 如果游戏胜利，播放胜利音效
        if (gameState === 3) {
            setTimeout(() => {
                musicManager.playSuccessSound()
                setShowGameEndTip(true)
            }, 3000);
        }
    }, [gameState]);

    // 设置游戏状态
    const handleGameState = (state: number) => {
        setGameState(state)
    }

    // 向单词列表添加单词
    const addWordToInputList = (word: string) => {
        const newWordList = [word, ...inputWords];
        setInputWords([...newWordList])
        // 每次添加完都判断一次错误单词
        checkWrongLetters(word, newWordList)
    }

    // 判定输入的单词是否正确，并更新错误单词列表
    const checkWrongLetters = (word: string, wordList: string[]) => {
        // 首先判断是否正确，如果正确，游戏结束
        if (correctWord === word.toLowerCase()) {
            // 赢了
            handleGameState(3)
            // 游戏结束调用接口
            sendVsPlayerGameStatus(3, wordList);
            return
        } else {
            // 如果不正确，就扣除3分钟也就是180秒
            const seconds = countDownSeconds;
            if (seconds <= 180) {
                setCountDownSeconds(0)
                // 输了
                handleGameState(2)
                // 游戏结束调用接口-这个由自动检测触发

            } else {
                setCountDownSeconds(seconds - 180)
            }
        }

        const letters = word.toLowerCase().split('')

        letters.forEach(letter => {
            if (!correctWord?.includes(letter)) {
                setWrongLetters(prevWrongLetters => [...prevWrongLetters, letter.toLowerCase()]);
            }
        })
    }

    // 在设置中点击游戏提示
    const showPlayTipModal = () => {
        // 关闭设置弹窗
        setShowSetting(false)
        // 显示游戏提示
        setShowPlayTip(true)
    }

    // 返回按钮的点击事件
    const handleBackButton = async () => {
        // 弹出提示

        switchPage(PageEnum.VS_PLAYER_HOME)
    }

    // 胜利界面card-挑战模式的背景图样式
    const successCardStyle = (gameState === 3 || gameState === 2) ? {
        backgroundImage: `url(${imgUrls.dailyWinCardBgUrl})`,
        transition: 'background-image 1s ease-in-out'
    } : {};


    // 关闭游戏的结束弹窗，直接返回home
    const closeGameEndModal = () => {
        // GA埋点

        setShowGameEndTip(false)
        switchPage(PageEnum.VS_PLAYER_HOME)
    }

    // 打开how to play提示弹窗
    const openHowToPlayModal = () => {
        setShowPlayTip(true)
    }

    // 打开设置弹窗
    const openSettingModal = () => {
        // GA埋点
        setShowSetting(true)
    }

    return (
        <div ref={targetRef} className="height-100-percent width-100-percent touch-action-none">
            <div className="global-page-background-img"
                 style={{backgroundImage: `url(${imgUrls.gamePageBgIconUrl})`}}/>
            {/*顶部标题栏*/}
            <div className="top-title-container"
                 style={{backgroundImage: `url(${imgUrls.titleBarBgUrl})`}}>
                <img onClick={handleBackButton} className="height-30 width-30 margin-left-13 object-fit-contain"
                     src={imgUrls.goBackIconUrl} alt="pic"/>
                <div
                    className="font-size-20 color-white text-center position-fixed position-left-50-percent transform-rotate-minus-50 my-roboto-medium">
                    vs Player
                </div>
                <img onClick={openHowToPlayModal} className="height-30 width-30 object-fit-contain margin-left-auto"
                     src={imgUrls.gameTipIconUrl}
                     alt="pic"/>
                <img onClick={openSettingModal}
                     className="height-30 width-30 object-fit-contain margin-left-8 margin-right-10"
                     src={imgUrls.settingIconUrl}
                     alt="pic"/>
            </div>
            {/* 倒计时进度框 */}
            <CountDownBar gameState={gameState} handleGameState={handleGameState}
                          inputWords={inputWords}
                          countDownSeconds={countDownSeconds}
                          setCountDownSeconds={setCountDownSeconds}/>
            {/*九宫格字母界面card*/}
            <div>
                <div
                    className={`margin-top-30`}
                    style={successCardStyle}
                >
                    {/*游戏九宫格界面*/}
                    <GridLetterCard wordList={inputWords} correctWord={correctWord ?? ''}
                    />
                </div>
                <Keyboard
                    hasHint={hasHint}
                    setHasHint={(value) => setHasHint(value)}
                    hintLetter={hintLetter}
                    setHintLetter={setHintLetter}
                    correctWord={correctWord ?? ''}
                    inputWords={inputWords}
                    gameState={gameState}
                    handleGameState={handleGameState}
                    addWordToInputList={addWordToInputList}
                    wrongLetters={wrongLetters}/>
            </div>
            {/*daily游戏结束时的弹窗*/}
            <VsPlayGameEndDialog key={"dailyGameFailDialog"} isOpen={showGameEndTip}
                                 roomResult={gameOverResult!}
                                 onClose={closeGameEndModal}/>
            {/*设置的弹窗*/}
            <SettingModal key="homeSettingModal" isOpen={showSetting} onClose={() => setShowSetting(false)}
                          onShowPlayTip={showPlayTipModal}/>
            {/*游戏提示的弹窗*/}
            <PlayTipModal key="playTipModal" isOpen={showPlayTip} onClose={() => setShowPlayTip(false)}/>
        </div>
    );
};

export default VsPlayerGame;
